import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ImageOff } from 'lucide-react';
import { Skeleton } from 'src/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { RawDataPoint } from 'src/redux/admin/reporting/reporting.types';

export interface CreativePerformanceTableProps {
  data: RawDataPoint[];
  isLoading: boolean;
  shouldShowMetric: (metric: string) => boolean;
}

interface AggregatedCreativeRow {
  creative_id: number;
  creative_name: string;
  media: string | null;
  creative_type: string | null;
  layout: string | null;
  impressions: number;
  clicks: number;
  views: number;
  spend: number;
  conversions: number;
  video_starts: number;
  video_completes: number;
  engagements: number;
  completions: number;
  ctr: number;
  cpc: number;
  cpm: number;
  cpa: number;
  vcr: number;
}

type SortKey = keyof AggregatedCreativeRow;
type SortOrder = 'asc' | 'desc';

const safeDiv = (numerator: number, denominator: number, multiplier = 1): number => {
  if (denominator === 0) return 0;
  return (numerator / denominator) * multiplier;
};

const formatNumber = (num: number, decimals = false): string => {
  if (!isFinite(num) || isNaN(num)) return 'N/A';
  if (decimals) {
    return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  return num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const formatCurrency = (value: number): string => {
  if (!isFinite(value) || isNaN(value)) return 'N/A';
  return `$${formatNumber(value, true)}`;
};

const formatPercentage = (value: number): string => {
  if (!isFinite(value) || isNaN(value)) return 'N/A';
  return `${formatNumber(value, true)}%`;
};

const hasMetricValue = (rows: AggregatedCreativeRow[], key: SortKey): boolean =>
  rows.some((row) => {
    const value = row[key];
    return value !== null && value !== undefined && value !== 0;
  });

type ColumnConfig = {
  key: SortKey;
  label: string;
  metricKey?: string;
  alwaysShow?: boolean;
  align?: 'left' | 'right';
  format: (row: AggregatedCreativeRow) => string;
};

const CreativePlaceholder: React.FC = () => (
  <span
    className="flex size-10 shrink-0 items-center justify-center rounded border border-gray-300 bg-gray-100 text-gray-400"
    aria-hidden="true"
  >
    <ImageOff className="size-4" />
  </span>
);

const CreativeThumbnail: React.FC<{
  media: string | null;
  creativeName: string;
}> = ({ media, creativeName }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  if (!media || imageFailed) {
    return <CreativePlaceholder />;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className="shrink-0 rounded border border-gray-300 bg-white p-0.5 hover:opacity-90 transition-opacity"
        aria-label={`View ${creativeName}`}
      >
        <img
          src={media}
          alt=""
          className="size-10 rounded object-contain"
          onError={() => setImageFailed(true)}
        />
      </button>
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-[95vw] gap-4">
          <DialogHeader>
            <DialogTitle>{creativeName}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <img
              src={media}
              alt={creativeName}
              className="max-h-[85vh] max-w-full object-contain"
              onError={() => setImageFailed(true)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const SortIcon: React.FC<{ active: boolean; order: SortOrder }> = ({ active, order }) => {
  if (!active) return <ArrowUpDown className="ml-1 inline size-3 opacity-50" />;
  return order === 'asc'
    ? <ArrowUp className="ml-1 inline size-3" />
    : <ArrowDown className="ml-1 inline size-3" />;
};

const CreativePerformanceTable: React.FC<CreativePerformanceTableProps> = ({
  data,
  isLoading,
  shouldShowMetric,
}) => {
  const [sortBy, setSortBy] = useState<SortKey>('impressions');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const aggregatedRows = useMemo((): AggregatedCreativeRow[] => {
    const creativesMap = new Map<number, AggregatedCreativeRow>();

    data.forEach((row) => {
      if (row.creative_id == null) {
        return;
      }

      const creativeId = row.creative_id;
      let creative = creativesMap.get(creativeId);

      if (!creative) {
        creative = {
          creative_id: creativeId,
          creative_name: row.creative_name ?? '',
          media: row.media ?? null,
          creative_type: typeof row.creative_type === 'string' ? row.creative_type : null,
          layout: typeof row.layout === 'string' ? row.layout : null,
          impressions: 0,
          clicks: 0,
          views: 0,
          spend: 0,
          conversions: 0,
          video_starts: 0,
          video_completes: 0,
          engagements: 0,
          completions: 0,
          ctr: 0,
          cpc: 0,
          cpm: 0,
          cpa: 0,
          vcr: 0,
        };
        creativesMap.set(creativeId, creative);
      }

      if (!creative.creative_name && row.creative_name) {
        creative.creative_name = row.creative_name;
      }
      if (!creative.media && row.media) {
        creative.media = row.media;
      }
      if (!creative.creative_type && typeof row.creative_type === 'string') {
        creative.creative_type = row.creative_type;
      }
      if (!creative.layout && typeof row.layout === 'string') {
        creative.layout = row.layout;
      }

      creative.impressions += row.impressions ?? 0;
      creative.clicks += row.clicks ?? 0;
      creative.views += row.views ?? 0;
      creative.spend += row.spend ?? 0;
      creative.conversions += row.conversions ?? 0;
      creative.video_starts += row.video_starts ?? 0;
      creative.video_completes += row.video_completes ?? 0;
      creative.engagements += Number(row.engagements ?? 0);
      creative.completions += Number(row.completions ?? 0);
    });

    return Array.from(creativesMap.values()).map((creative) => ({
      ...creative,
      ctr: safeDiv(creative.clicks, creative.impressions, 100),
      cpc: safeDiv(creative.spend, creative.clicks),
      cpm: safeDiv(creative.spend, creative.impressions, 1000),
      cpa: safeDiv(creative.spend, creative.conversions),
      vcr: safeDiv(creative.video_completes, creative.video_starts, 100),
    }));
  }, [data]);

  const allColumns: ColumnConfig[] = useMemo(() => [
    {
      key: 'creative_name',
      label: 'Creative',
      alwaysShow: true,
      align: 'left',
      format: () => '',
    },
    {
      key: 'impressions',
      label: 'Impressions',
      metricKey: 'impressions',
      align: 'right',
      format: (row) => formatNumber(row.impressions),
    },
    {
      key: 'clicks',
      label: 'Clicks',
      metricKey: 'clicks',
      align: 'right',
      format: (row) => formatNumber(row.clicks),
    },
    {
      key: 'ctr',
      label: 'CTR',
      metricKey: 'ctr',
      align: 'right',
      format: (row) => formatPercentage(row.ctr),
    },
    {
      key: 'spend',
      label: 'Spend',
      metricKey: 'spend',
      align: 'right',
      format: (row) => formatCurrency(row.spend),
    },
    {
      key: 'cpm',
      label: 'CPM',
      metricKey: 'cpm',
      align: 'right',
      format: (row) => formatCurrency(row.cpm),
    },
    {
      key: 'conversions',
      label: 'Conversions',
      metricKey: 'conversions',
      align: 'right',
      format: (row) => formatNumber(row.conversions),
    },
    {
      key: 'cpa',
      label: 'CPA',
      metricKey: 'cpa',
      align: 'right',
      format: (row) => formatCurrency(row.cpa),
    },
    {
      key: 'video_starts',
      label: 'Video Starts',
      metricKey: 'video_starts',
      align: 'right',
      format: (row) => formatNumber(row.video_starts),
    },
    {
      key: 'video_completes',
      label: 'Video Completes',
      metricKey: 'video_completes',
      align: 'right',
      format: (row) => formatNumber(row.video_completes),
    },
    {
      key: 'vcr',
      label: 'VCR',
      metricKey: 'video_completion_rate',
      align: 'right',
      format: (row) => formatPercentage(row.vcr),
    },
    {
      key: 'engagements',
      label: 'Engagements',
      metricKey: 'engagements',
      align: 'right',
      format: (row) => formatNumber(row.engagements),
    },
    {
      key: 'completions',
      label: 'Completions',
      metricKey: 'completions',
      align: 'right',
      format: (row) => formatNumber(row.completions),
    },
  ], []);

  const visibleColumns = useMemo(
    () => allColumns.filter((column) => {
      if (column.alwaysShow) return true;
      if (column.metricKey && !shouldShowMetric(column.metricKey)) return false;
      return hasMetricValue(aggregatedRows, column.key);
    }),
    [allColumns, aggregatedRows, shouldShowMetric],
  );

  const sortedRows = useMemo(() => {
    return [...aggregatedRows].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
      }

      const aStr = String(aValue);
      const bStr = String(bValue);
      return sortOrder === 'desc' ? bStr.localeCompare(aStr) : aStr.localeCompare(bStr);
    });
  }, [aggregatedRows, sortBy, sortOrder]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(key);
    setSortOrder(key === 'creative_name' ? 'asc' : 'desc');
  };

  return (
    <div className="rounded-2xl bg-white border border-gray-400 p-8 flex-1">
      <div className="text-2xl font-light mb-4">
        Creative Performance
      </div>
      <div className="overflow-x-auto">
        {isLoading ? (
          <Skeleton className="w-full h-[200px]" />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-400">
                {visibleColumns.map((column) => (
                  <th
                    key={column.key}
                    className={`py-2 px-4 text-sm font-medium text-gray-600 ${
                      column.align === 'right' ? 'text-right' : 'text-left'
                    } cursor-pointer select-none hover:text-gray-900`}
                    onClick={() => handleSort(column.key)}
                    aria-sort={
                      sortBy === column.key
                        ? sortOrder === 'asc' ? 'ascending' : 'descending'
                        : 'none'
                    }
                  >
                    {column.label}
                    <SortIcon active={sortBy === column.key} order={sortOrder} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumns.length}
                    className="py-8 px-4 text-center text-sm text-gray-500"
                  >
                    No creative data available for this selection
                  </td>
                </tr>
              ) : (
                sortedRows.map((row) => (
                  <tr key={row.creative_id} className="border-b border-gray-200 hover:bg-gray-50">
                    {visibleColumns.map((column) => (
                      <td
                        key={column.key}
                        className={`py-2 px-4 ${
                          column.align === 'right' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {column.key === 'creative_name' ? (
                          <div className="flex items-center gap-3 min-w-0">
                            <CreativeThumbnail
                              media={row.media}
                              creativeName={row.creative_name || `Creative ${row.creative_id}`}
                            />
                            <span className="truncate">{row.creative_name || `Creative ${row.creative_id}`}</span>
                          </div>
                        ) : (
                          column.format(row)
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CreativePerformanceTable;
